import ArrowLeft16 from '@carbon/icons-react/es/arrow--left/16';
import PiggyBank24 from '@carbon/icons-react/es/piggy-bank/24';
import { FormInstance } from 'antd';
import { isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus } from 'api/account';
import {
  AvailablePlans,
  BillingCycle,
  BillingVersion,
  LicenseCapLimit,
  MonthlyPrice,
  PlanHierarchy,
  RestrictedPlans,
  Subscription,
  SubscriptionForm,
  SupportedPlans as Plans,
  UpgradeTo,
  YearlyPrice,
} from 'api/billing/types';
import { Button, ButtonProps } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { toastService } from 'components/molecules/toast';
import { useAccount, useIsAccountActive } from 'hooks/use-account';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { queryClient } from 'queries';
import {
  SUBSCRIPTION_KEY,
  useBillingVersion,
  useDowngradePlanMutation,
  usePlanName,
} from 'queries/billing';
import { logger } from 'services';
import { BillingHelpMessage } from './help-message';
import { DowngradeConfirmation } from './modals';
import { BillingUpgrade } from './upgrade';
import { getCurrentBillingCycle, getPlanName, getYearlySavings } from './utils';

const MINIMUM_LICENSE_QUANTITY = 5;

const FadedText = styled.div`
  opacity: 0.7;
`;

const QuantityFormItem = styled(Form.Item)`
  .ant-form-item-label {
    display: flex;
    align-items: center;
  }
`;

const QuantityLabelWrapper = styled.div`
  font-size: var(--font-size-base);
  margin-bottom: var(--spacing-02);
`;

const FormWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledForm = styled(Form)`
  .form-item-vertical {
    display: block;

    .ant-form-item-control {
      display: block;
    }
  }
`;

const TabWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;

  &.has-badge {
    TabContent {
      /* width: 306px; */
    }
  }
`;

const TabContent = styled.div`
  /* width: 305px; */
  height: 100%;
  border-top: 5px solid var(--green-50);
  box-shadow: var(--shadow-04);
  padding: var(--spacing-06);
  position: relative;
`;

const PlanTabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-01);
  margin-top: var(--spacing-07);
  margin-bottom: var(--layout-05);
`;

const PlanName = styled.p`
  font-size: var(--heading-5-size);
  font-weight: var(--font-weight-bold);
  margin: 0;
`;

const SelectedBillingCycleDetailsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding: var(--spacing-06) 0;
  font-size: var(--heading-6-size);
  color: var(--gray-80);

  .price {
    font-size: var(--heading-2-size);
    font-weight: var(--heading-2-weight);
    margin-right: var(--spacing-05);
    line-height: var(--heading-2-line-height);
    color: var(--gray-100);
  }
`;

const OtherBillingCycleDetailsWrapper = styled.div`
  padding: var(--spacing-06) 0;
  font-size: var(--heading-6-size);
  line-height: var(--heading-6-line-height);
  color: var(--gray-80);
  min-height: var(--layout-06);
`;

const StyledUpgradeButton = styled(Button)<
  ButtonProps & {
    isDowngrade?: boolean;
    isCurrentPlan?: boolean;
  }
>`
  width: 100%;

  &:not(:disabled) {
    background: ${(props) =>
      props.isDowngrade ? 'var(--red-60)' : 'var(--green-50) !important'};
    border-color: ${(props) =>
      props.isDowngrade ? 'var(--red-60)' : 'var(--green-50) !important'};
    color: var(--white-100);
  }

  &:hover:not(:disabled),
  &:active:not(:disabled),
  &:focus:not(:disabled) {
    background: ${(props) =>
      props.isDowngrade ? 'var(--red-60)' : 'var(--green-50) !important'};
    border-color: ${(props) =>
      props.isDowngrade ? 'var(--red-60)' : 'var(--green-50) !important'};
    color: var(--white-100);
  }
`;

const Link = styled.a`
  text-decoration: underline;
  color: inherit;
`;

const Divider = styled.div`
  margin: var(--spacing-07) 0;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
`;

const RecommendedBadge = styled.div`
  width: 100%;
  height: 18px;
  background: var(--green-50);
  font-size: var(--font-size-sm);
  color: var(--white-100);
  text-align: center;
  position: absolute;
  top: -18px;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  .list-header {
    margin-bottom: var(--spacing-06);

    > b {
      display: flex;
      align-items: flex-start;
      gap: var(--spacing-03);

      > span {
        line-height: var(--line-height-label-01);
      }
    }
  }

  li {
    margin-bottom: var(--spacing-03);
  }
`;

const Mandatory = styled.p`
  margin: 0;

  &:before {
    content: '*';
  }
`;

const SavingsMessage = styled.p`
  display: flex;
  gap: var(--spacing-03);
  align-items: flex-start;
  color: var(--green-50);
  margin: 0;
  min-height: calc(var(--layout-04) - var(--spacing-02));

  Button {
    color: inherit;
    font-size: inherit;
    padding: 0;
    height: auto;
    text-decoration: underline;
  }
`;

const ChangePlanButton = ({
  subscription,
  plan,
  quantity,
  billingCycle,
  onUpgrade,
  onDowngrade,
  isTrial,
  version,
}: {
  subscription: Subscription;
  plan: Plans;
  quantity: number;
  billingCycle: BillingCycle;
  onUpgrade: (plan: Plans, completeCallback?: () => void) => void;
  onDowngrade: (plan: Plans, completeCallback?: () => void) => void;
  isTrial: boolean;
  version: BillingVersion;
}) => {
  const translate = useTranslate();
  const isAccountActive = useIsAccountActive();
  const currentPlan = getPlanName(subscription.plan.planName);
  const currentCycle = getCurrentBillingCycle(subscription.plan.planName);
  const _planHierarchy = PlanHierarchy[version];
  const targetPlan = plan;
  const targetPlanName = usePlanName(targetPlan);
  const currentPlanName = usePlanName(currentPlan);

  const isDowngrade = _planHierarchy[currentPlan] > _planHierarchy[targetPlan];

  const isCurrentPlan =
    _planHierarchy[currentPlan] === _planHierarchy[targetPlan];

  const isCurrentCycle = currentCycle === billingCycle;

  const isCurrentPlanAndCycle = isCurrentPlan && isCurrentCycle;

  const buttonText = isDowngrade
    ? translate('billing.downgrade.to.plan.button', {
        planName: targetPlanName,
      })
    : isCurrentPlan && (isTrial || !isAccountActive)
    ? translate('billing.continue.with.plan.button', {
        planName: currentPlanName,
      })
    : isCurrentPlanAndCycle
    ? translate('billing.current.plan')
    : isCurrentPlan
    ? translate('billing.upgrade.to.yearly')
    : translate('billing.upgrade.to.plan.button', {
        planName: targetPlanName,
      });

  const [isLoading, setIsLoading] = useState(false);

  const onClick = useCallback(() => {
    if (isDowngrade) {
      onDowngrade(plan, () => setIsLoading(false));
    } else {
      onUpgrade(plan, () => setIsLoading(false));
    }
  }, [isDowngrade, onDowngrade, onUpgrade, plan]);

  const isDisabled = useMemo(() => {
    switch (plan) {
      case Plans.Enterprise:
      case Plans['Enterprise-V2']:
        return billingCycle === BillingCycle.Monthly;

      case Plans.Professional:
        return quantity > LicenseCapLimit.Professional;

      case Plans.Essentials:
        return quantity > LicenseCapLimit.Essentials;

      default:
        return false;
    }
  }, [billingCycle, plan, quantity]);

  return (
    <StyledUpgradeButton
      type="primary"
      onClick={onClick}
      loading={isLoading}
      disabled={
        isDisabled || (!isTrial && isAccountActive && isCurrentPlanAndCycle)
      }
      danger={isDowngrade}
      isDowngrade={isDowngrade}
    >
      {buttonText}
    </StyledUpgradeButton>
  );
};

const KeyFeatureList = ({ plan }: { plan: Plans }) => (
  <List>
    <li className="list-header">
      <b>
        <FormattedMessage
          id={`billing.plan.${plan}.key.feature.list.title`.toLowerCase()}
          values={{
            arrowLeftIcon: <ArrowLeft16 />,
            span: (chunks) => <span>{chunks}</span>,
          }}
        />
      </b>
    </li>
    <FormattedMessage
      id={`billing.plan.${plan}.key.feature.list.content`.toLowerCase()}
      values={{
        li: (chunks) => <li>{chunks}</li>,
      }}
    />
  </List>
);

const QuantityInputLabel = () => (
  <QuantityLabelWrapper>
    <FormattedMessage id="billing.team_size.input.label" />
  </QuantityLabelWrapper>
);

const SubscriptionFormComponent = ({
  form,
  onFormValuesChange,
  currentCycle,
  licenseQuantity,
}: {
  form: FormInstance<SubscriptionForm>;
  onFormValuesChange: (changedValues) => void;
  currentCycle: BillingCycle;
  licenseQuantity: number;
}) => {
  const { status: accountStatus } = useAccount();

  const minQuantity = useMemo(
    () => Math.max(licenseQuantity ?? 0, MINIMUM_LICENSE_QUANTITY),
    [licenseQuantity],
  );

  const onQuantityChange = useCallback(
    (value) => {
      if (!isNumber(value) || value < minQuantity) {
        form.setFieldsValue({
          quantity: minQuantity,
        });
      }
    },
    [form, minQuantity],
  );

  return (
    <FormWrapper>
      <StyledForm
        form={form}
        colon={false}
        layout="inline"
        style={{ alignItems: 'center' }}
        initialValues={{
          quantity: minQuantity,
          billing_cycle: BillingCycle.Yearly,
        }}
        onValuesChange={onFormValuesChange}
      >
        <QuantityFormItem
          label={<QuantityInputLabel />}
          labelAlign="left"
          name="quantity"
        >
          <InputNumber
            min={minQuantity}
            type="number"
            onChange={onQuantityChange}
          />
        </QuantityFormItem>
        <Form.Item
          label={<FormattedMessage id="billing.cycle.label" />}
          name="billing_cycle"
          className="form-item-vertical"
        >
          <Radio.Group>
            <Radio value={BillingCycle.Yearly}>
              <FormattedMessage id="billing.cycle.option.yearly" />
            </Radio>
            {accountStatus === AccountStatus.Trial ||
            currentCycle === BillingCycle.Monthly ? (
              <Radio value={BillingCycle.Monthly}>
                <FormattedMessage id="billing.cycle.option.monthly" />
              </Radio>
            ) : null}
          </Radio.Group>
        </Form.Item>
      </StyledForm>
      <div>
        <BillingHelpMessage />
      </div>
    </FormWrapper>
  );
};

const SelectedBillingCycleDetails = ({
  plan,
  billingCycle,
}: {
  plan: Plans;
  billingCycle: string;
}) => {
  return (
    <SelectedBillingCycleDetailsWrapper>
      <span className="price">
        $
        {billingCycle === BillingCycle.Yearly ||
        [Plans.Enterprise, Plans['Enterprise-V2']].includes(plan)
          ? YearlyPrice[plan]
          : MonthlyPrice[plan]}
      </span>
      <div>
        <FormattedMessage
          id={`billing.cycle.${billingCycle}.duration.text`}
          values={{
            br: () => <br />,
            plan: plan.toLowerCase(),
          }}
        />
      </div>
    </SelectedBillingCycleDetailsWrapper>
  );
};

const OtherBillingCycleDetails = ({
  billingCycle,
  plan,
  onAnnualCycleOptIn,
  quantity,
  currentBillingCycle,
  accountStatus,
}: {
  billingCycle: BillingCycle;
  plan: Plans;
  onAnnualCycleOptIn: () => void;
  quantity: number;
  currentBillingCycle: BillingCycle;
  accountStatus: AccountStatus;
}) => {
  const phoneNumber = useTranslation('billing.phone.number');
  const demoLink = useTranslation('billing.demo.link');
  const otherBillingCycle =
    billingCycle === BillingCycle.Yearly
      ? BillingCycle.Monthly
      : BillingCycle.Yearly;

  return (
    <OtherBillingCycleDetailsWrapper>
      {(() => {
        switch (plan) {
          case Plans.Enterprise:
          case Plans['Enterprise-V2']:
            return (
              <FadedText>
                <Link href={demoLink} target="_blank" rel="noreferrer">
                  <FormattedMessage id="billing.book.demo.text" />
                </Link>
                &nbsp;
                <FormattedMessage id="or" />
                <br />
                <FormattedMessage id="billing.call_us" />
                &nbsp;
                <Link href={`tel:${phoneNumber}`}>{phoneNumber}</Link>
              </FadedText>
            );
          case Plans.Premium:
          case Plans['Premium-V2']:
            return (
              <>
                {currentBillingCycle !== BillingCycle.Yearly ||
                accountStatus === AccountStatus.Trial ? (
                  <SavingsMessage>
                    <PiggyBank24 />
                    {billingCycle === BillingCycle.Yearly && (
                      <FormattedMessage
                        id="billing.yearly.saving.message"
                        values={{
                          savings: getYearlySavings(plan, quantity),
                        }}
                      />
                    )}
                    {billingCycle === BillingCycle.Monthly && (
                      <Button type="link" onClick={onAnnualCycleOptIn}>
                        <FormattedMessage
                          id="billing.yearly.save.message"
                          values={{
                            savings: getYearlySavings(plan, quantity),
                          }}
                        />
                      </Button>
                    )}
                  </SavingsMessage>
                ) : null}
              </>
            );
          default:
            return (
              <FadedText>
                <FormattedMessage id="or" />
                &nbsp; $
                {billingCycle === BillingCycle.Yearly
                  ? MonthlyPrice[plan]
                  : YearlyPrice[plan]}
                <FormattedMessage
                  id="billing.other.cycle.duration.text"
                  values={{
                    billingCycle: otherBillingCycle,
                  }}
                />
                <br />
                {[Plans.Essentials, Plans.Standard].includes(plan) ? (
                  <Mandatory>
                    <FormattedMessage id="billing.min.members.details" />
                  </Mandatory>
                ) : plan === Plans.Professional ? (
                  <Mandatory>
                    <FormattedMessage id="billing.max.members.details" />
                  </Mandatory>
                ) : null}
              </FadedText>
            );
        }
      })()}
    </OtherBillingCycleDetailsWrapper>
  );
};

const DefaultFeatureList = () => (
  <List>
    <FormattedMessage
      id="billing.default.feature.list"
      values={{
        li: (chunks) => <li>{chunks}</li>,
        b: (chunks) => <b>{chunks}</b>,
      }}
    />
  </List>
);

const PlanTab = ({
  subscription,
  plan,
  billingCycle,
  onUpgrade,
  onDowngrade,
  onAnnualCycleOptIn,
  quantity,
}: {
  subscription: Subscription;
  plan: Plans;
  billingCycle: BillingCycle;
  onUpgrade: (plan: Plans, completeCallback?: () => void) => void;
  onDowngrade: (plan: Plans, completeCallback?: () => void) => void;
  onAnnualCycleOptIn: () => void;
  quantity: number;
}) => {
  const { status: accountStatus } = useAccount();
  const version = useBillingVersion();
  const showRecommendedBadge =
    [Plans.Premium, Plans['Premium-V2']].includes(plan) &&
    ![
      Plans.Enterprise,
      Plans['Enterprise-V2'],
      RestrictedPlans['Enterprise-Tiered'],
      RestrictedPlans['Enterprise-Tiered_V2'],
    ].includes(getPlanName(subscription.plan.planName));

  if (!version) {
    return <></>;
  }

  return (
    <TabWrapper className={showRecommendedBadge ? 'has-badge' : ''}>
      {showRecommendedBadge && (
        <RecommendedBadge>
          <FormattedMessage id="billing.recommended.badge.text" />
        </RecommendedBadge>
      )}
      <TabContent>
        <PlanName>
          <FormattedMessage id={`billing.plan.${plan}`.toLowerCase()} />
        </PlanName>
        <SelectedBillingCycleDetails plan={plan} billingCycle={billingCycle} />
        <OtherBillingCycleDetails
          plan={plan}
          billingCycle={billingCycle}
          quantity={quantity}
          onAnnualCycleOptIn={onAnnualCycleOptIn}
          currentBillingCycle={getCurrentBillingCycle(
            subscription.plan.planName,
          )}
          accountStatus={accountStatus}
        />

        <ChangePlanButton
          subscription={subscription}
          onUpgrade={onUpgrade}
          onDowngrade={onDowngrade}
          plan={plan}
          quantity={quantity}
          billingCycle={billingCycle}
          isTrial={accountStatus === AccountStatus.Trial}
          version={version}
        />

        <Divider />
        <DefaultFeatureList />
        <Divider />
        <KeyFeatureList plan={plan} />
      </TabContent>
    </TabWrapper>
  );
};

const PlanTabs = ({
  subscription,
  billingCycle,
  onUpgrade,
  onDowngrade,
  onAnnualCycleOptIn,
  quantity,
}: {
  subscription: Subscription;
  billingCycle: BillingCycle;
  onUpgrade: (plan: Plans, completeCallback?: () => void) => void;
  onDowngrade: (plan: Plans, completeCallback?: () => void) => void;
  onAnnualCycleOptIn: () => void;
  quantity: number;
}) => {
  const version = useBillingVersion();

  if (!version) {
    return <></>;
  }
  const availablePlans = AvailablePlans[version];

  return (
    <PlanTabContainer>
      {availablePlans.map((plan) => (
        <PlanTab
          subscription={subscription}
          onAnnualCycleOptIn={onAnnualCycleOptIn}
          onUpgrade={onUpgrade}
          onDowngrade={onDowngrade}
          plan={Plans[plan]}
          billingCycle={billingCycle}
          quantity={quantity}
        />
      ))}
    </PlanTabContainer>
  );
};

export const PlanSelection = ({
  subscription,
  close,
}: {
  subscription: Subscription;
  close?: () => void;
}) => {
  const { status: accountStatus } = useAccount();
  const isAccountActive = useIsAccountActive();
  const isTrial = useMemo(
    () => accountStatus === AccountStatus.Trial,
    [accountStatus],
  );
  const [form] = Form.useForm();
  const [quantity, setQuantity] = useState(subscription.licenseQuantity);
  const [billingCycle, setBillingCycle] = useState(BillingCycle.Yearly);
  const [upgradeTo, setUpgradeTo] = useState<UpgradeTo | null>(null);
  const currentPlan = useMemo(
    () => getPlanName(subscription.plan.planName),
    [subscription.plan.planName],
  );
  const currentCycle = useMemo(
    () => getCurrentBillingCycle(subscription.plan.planName),
    [subscription.plan.planName],
  );
  const [downgradeToPlan, setDowngradeToPlan] = useState<null | Plans>(null);

  const downgradeToPlanName = usePlanName(downgradeToPlan);

  const downgradeSuccessTitle = useTranslation(
    'billing.downgrade.scheduled.success.title',
  );
  const translate = useTranslate();

  const { mutateAsync: downgradePlan, isLoading: isDowngrading } =
    useDowngradePlanMutation();

  useEffect(() => {
    form.setFieldsValue({
      quantity: subscription?.licenseQuantity,
      billingCycle: BillingCycle.Yearly,
    });
  }, [subscription, form]);

  const onFormValuesChange = useCallback((changedValues) => {
    if (changedValues.billing_cycle) {
      setBillingCycle(changedValues.billing_cycle);
    }
    if (changedValues.quantity) {
      setQuantity(changedValues.quantity);
    }
  }, []);

  const onDismiss = useCallback(() => setUpgradeTo(null), []);

  const onAnnualCycleOptIn = useCallback(() => {
    form.setFieldsValue({
      billing_cycle: BillingCycle.Yearly,
    });
    /**
     * calling `setFieldsValue` doesnt trigger onChange event
     * hence explicitly calling setter method.
     * Ref: https://ant.design/components/form/#setFieldsValue-do-not-trigger-onFieldsChange-or-onValuesChange
     */
    setBillingCycle(BillingCycle.Yearly);
  }, [form]);

  const onUpgrade = useCallback(
    async (plan: Plans) => {
      if (!subscription) {
        return;
      }
      const formValues: SubscriptionForm = await form.validateFields();
      let _upgradeTo: UpgradeTo = {};

      if (isTrial || !isAccountActive) {
        _upgradeTo.plan = plan;
      } else {
        _upgradeTo = {
          billingCycle: formValues.billing_cycle,
          licenseCount: formValues.quantity,
        };

        if (plan !== currentPlan) {
          _upgradeTo.plan = plan;
        }
      }

      setUpgradeTo(_upgradeTo);
    },
    [currentPlan, form, isAccountActive, isTrial, subscription],
  );

  const onDowngrade = useCallback((plan: Plans) => {
    setDowngradeToPlan(plan);
  }, []);

  const onDowngradeConfirm = useCallback(async () => {
    if (!downgradeToPlan || !subscription) {
      return;
    }
    if (isTrial || !isAccountActive) {
      setUpgradeTo({
        plan: downgradeToPlan,
      });
      setDowngradeToPlan(null);

      return;
    }
    const formValues: SubscriptionForm = await form.validateFields();

    try {
      await downgradePlan({
        plan: downgradeToPlan,
        form: formValues,
        subscription,
      });
      close?.();
      toastService({
        title: downgradeSuccessTitle,
        message: translate('billing.downgrade.scheduled.success.message', {
          planName: downgradeToPlanName,
        }),
      }).success();
      queryClient.invalidateQueries(SUBSCRIPTION_KEY);
    } catch (e) {
      logger.error('Failed to downgrade plan', { error: e });
    }
  }, [
    close,
    downgradePlan,
    downgradeSuccessTitle,
    downgradeToPlan,
    downgradeToPlanName,
    form,
    isAccountActive,
    isTrial,
    subscription,
    translate,
  ]);

  return (
    <>
      <SubscriptionFormComponent
        currentCycle={currentCycle}
        licenseQuantity={subscription.licenseQuantity}
        form={form}
        onFormValuesChange={onFormValuesChange}
      />
      <PlanTabs
        subscription={subscription}
        billingCycle={billingCycle}
        onAnnualCycleOptIn={onAnnualCycleOptIn}
        onUpgrade={onUpgrade}
        onDowngrade={onDowngrade}
        quantity={quantity}
      />
      {upgradeTo !== null && (
        <BillingUpgrade
          upgradeTo={upgradeTo}
          subscription={subscription}
          onDismiss={onDismiss}
        />
      )}
      {downgradeToPlan && (
        <DowngradeConfirmation
          currentPlan={currentPlan}
          targetPlan={downgradeToPlan}
          subscriptionEndDate={subscription.endDate}
          isDowngrading={isDowngrading}
          onOk={onDowngradeConfirm}
          onCancel={() => setDowngradeToPlan(null)}
        />
      )}
    </>
  );
};
